import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';

/**
 * Customizable input used throughout the app (but mostly for the Nursing Assessments tab).
 * Input can be rendered as a text, textarea, numerical, select, or date input.
 * This component makes it simple to implement an input paired with a "Within Normal Limits" checkbox,
 * which when checked, disables the input and fills it with a provided default value.
 *
 * To see examples where this component is used, visit the any of the classes that extend the
 * AssessmentFormComponent class (e.g. Neurological, Gastrointestinal, Cardiovascular).
 *
 * Note: WNL = "Within Normal Limits"
 */
@Component({
  selector: 'emr-input',
  templateUrl: './emr-input.component.html',
  styleUrls: ['./emr-input.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    TextareaModule,
    InputNumberModule,
    Select,
    DatePicker,
    CheckboxModule,
  ],
})
export class EmrInputComponent implements OnChanges {
  /**
   * The type of input to display.
   *
   * Available options: "text", "number", "select", "date", "time"
   */
  @Input() type: 'text' | 'textarea' | 'number' | 'select' | 'date' | 'time' = 'text';

  /**
   * Controls whether or not to display the "WNL" checkbox next to the input.
   */
  @Input() hasWNL = false;

  /**
   * Controls the width of the p-input element that is displayed.
   */
  @Input() width = '100%';

  /**
   * Label displayed above the input field that describes what the input is for.
   */
  @Input() label = '';

  /**
   * Text displayed within the input field when nothing has been entered.
   */
  @Input() placeholder = '';

  /**
   * Hint that appears under the input box.
   */
  @Input() hint?: string;

  /**
   * The FormControl to get/set the value for.
   */
  @Input() control: UntypedFormControl = new UntypedFormControl();

  /**
   * The value that should be automatically entered into the input field
   * when the "Within Normal Limits" checkbox is checked.
   */
  @Input() default: any;

  /**
   * Used to toggle whether the form control is within normal limits or not.
   */
  @Input() wnl = false;

  /**
   * The minimum value that can be entered in the input.
   * Only used with the "number" type.
   */
  @Input() min = 0;

  /**
   * The maximum value that can be entered in the input.
   * Only used with the "number" type.
   */
  @Input() max = 9999999999;

  /**
   * The options to display in the list the user can select from.
   * Only used with the "select" type.
   */
  @Input() options: FormOption[] = [];

  /**
   * The text to display in the p-dropdown if there are no options.
   * Only used with the "select" type.
   */
  @Input() emptyMessage = 'No data';

  /**
   * Whether the input is a required input. This is used primarily to
   * display a '*' character next to the label above the input field
   * to indicate it is required.
   */
  @Input() required = false;

  /**
   * Whether the component should have a blank space below the input field.
   * This is used primarily to align the input field with other input fields
   * that have hints displayed on the bottom.
   */
  @Input() bottomSpace = false;

  /**
   * Emits when the "WNL" checkbox is changed.
   */
  @Output() wnlChange = new EventEmitter<boolean>();

  /**
   * Run when the "Set All WNL" checkbox has been set to true, so that
   * the input's value can be set to the default value.
   */
  ngOnChanges(): void {
    if (this.hasWNL) {
      this.toggleWNL();
    }
  }

  /**
   * Toggles the wnl property. Enables/disables input and autofills/clears
   * the default value depending on the value of WNL checkbox.
   */
  toggleWNL() {
    if (this.wnl) {
      this.toDefaultValue();
    } else {
      this.clearControl();
    }
  }

  /**
   * Disables the FormControl and sets its value to be the default value.
   * Emits the value of the wnl property for the parent to handle the
   * "Set All WNL" checkbox display.
   */
  toDefaultValue() {
    // use {onlySelf: true} to restrict validity checking to the current control,
    // rather than the entire form
    this.control?.setValue(this.default, { onlySelf: true });
    this.control?.disable({ onlySelf: true });
    this.wnlChange.emit(this.wnl);
  }

  /**
   * Enables the FormControl and clears its value.
   * Emits the value of the wnl property for the parent to handle the
   * "Set All WNL" checkbox display.
   */
  clearControl() {
    // Enable control and clear it of any value
    this.control?.enable({ onlySelf: true });
    this.control?.setValue('', { onlySelf: true });
    this.wnlChange.emit(this.wnl);
  }

  /**
   * Event handler that is called whenever the input has been changed,
   * if the input is the "select" type. This is used to toggle the
   * "WNL" checkbox whenever the default/WNL option has been selected.
   *
   * @param event The value that the select input has been changed to.
   */
  selectionChanged(event: any) {
    if (event.value === this.default) {
      this.wnl = !this.wnl;
      this.toDefaultValue();
    }
  }

  get hasHint() {
    return this.hint !== undefined && this.hint !== null && this.hint !== '';
  }

  get hasBottomSpace() {
    return this.bottomSpace;
  }
}
